import { useLayoutEffect, useRef, useState } from "react";

const NEAR_BOTTOM_PX = 80;

// Scroll behavior of a message list (shared by the customer widget and the
// admin inbox): open at the bottom, follow new content only while the reader
// is at the bottom (or wrote it), otherwise offer a "new messages" jump, and
// keep the visible messages in place when older ones are prepended.
//
//   id             conversation shown (reset on change)
//   lastSeq        sequence number of the newest message
//   count          loaded messages (re-run trigger)
//   pendingCount   messages the viewer is sending (always follow those)
//   ownLast        the newest message was written by the viewer
//   prepended      value that changes when older messages were inserted
export function useStickyScroll({ id, lastSeq, count, pendingCount = 0, ownLast = false, prepended }) {
  const scroller = useRef(null);
  const atBottom = useRef(true);
  const seenSeq = useRef(0);
  const firstPaint = useRef(true);
  const anchor = useRef(null);
  const [showNew, setShowNew] = useState(false);

  useLayoutEffect(() => {
    firstPaint.current = true;
    atBottom.current = true;
    seenSeq.current = 0;
    setShowNew(false);
  }, [id]);

  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (firstPaint.current && count) {
      el.scrollTop = el.scrollHeight;
      firstPaint.current = false;
      seenSeq.current = lastSeq;
    } else if (lastSeq > seenSeq.current || pendingCount > 0) {
      if (atBottom.current || ownLast || pendingCount > 0) {
        el.scrollTop = el.scrollHeight;
        setShowNew(false);
      } else if (lastSeq > seenSeq.current) {
        setShowNew(true);
      }
      seenSeq.current = Math.max(seenSeq.current, lastSeq);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSeq, count, pendingCount]);

  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && anchor.current && prepended) {
      el.scrollTop = el.scrollHeight - anchor.current.height + anchor.current.top;
      anchor.current = null;
    }
  }, [prepended]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (atBottom.current) setShowNew(false);
  };

  // Call right before loading older messages.
  const rememberPosition = () => {
    const el = scroller.current;
    if (el) anchor.current = { height: el.scrollHeight, top: el.scrollTop };
  };

  const jumpToBottom = () => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
    setShowNew(false);
  };

  return { scroller, onScroll, showNew, jumpToBottom, rememberPosition };
}
